import { cloneDeep, get, omit } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { flatten } from "utils/objectUtilities";
import { FormRenderer } from ".";
import { dynamicFormFieldValidator } from "./helpers";

export const ConditionalFormRenderer = ({
  parentFieldName,
  conditionFieldName,
  conditionalFields,
  fallBackFields,
  getDefaultValues,
  onChange,
  cacheable = false,
}) => {
  const prevFieldsRef = useRef();
  const prevFieldValueRef = useRef();
  const prevTouchedRef = useRef();
  const prevValueRef = useRef();

  const cache = useRef(new Map());

  const form = useFormContext();
  const parentName = `${
    parentFieldName ? parentFieldName + "." : parentFieldName
  }`;
  const name = `${parentName}${conditionFieldName}`;

  const fieldValue = useWatch({
    control: form.control,
    name,
  });

  const { isDirty, isTouched } = form.getFieldState(name);

  useEffect(() => {
    if (form && onChange && isDirty) {
      onChange(form);
    }
  }, [fieldValue]); // eslint-disable-line

  let fields = useMemo(() => {
    let memoizedFields = conditionalFields[fieldValue];
    if (!memoizedFields)
      if (fallBackFields) memoizedFields = fallBackFields;
      else memoizedFields = [];

    return memoizedFields;
  }, [fieldValue]); // eslint-disable-line

  useEffect(() => {
    if (prevTouchedRef.current && !isTouched) {
      const keys = [...cache.current.keys()];
      for (const key of keys) {
        if (key !== String(fieldValue)) {
          cache.current.delete(key);
        }
      }
    }

    prevTouchedRef.current = isTouched;
  }, [isTouched]); // eslint-disable-line

  useEffect(() => {
    if (cacheable) {
      if (prevFieldValueRef.current) {
        const values = cloneDeep(
          parentFieldName.length
            ? form.getValues(parentFieldName)
            : form.getValues()
        );
        let prevFields = conditionalFields[prevFieldValueRef.current];
        if (values) {
          const tmpValues = isTouched ? values : prevValueRef.current;
          const keys = Object.keys(
            dynamicFormFieldValidator(prevFields, tmpValues)
          );
          let cacheValue = {};
          for (let key of keys) {
            cacheValue[key] = get(tmpValues, key);
          }
          if (isTouched || !!!cache.current.get(prevFieldValueRef.current)) {
            cache.current.set(prevFieldValueRef.current, { ...cacheValue });
          }
        }
      }
      prevFieldValueRef.current = String(fieldValue);
      prevValueRef.current = cloneDeep(
        parentFieldName.length
          ? form.getValues(parentFieldName)
          : form.getValues()
      );
    }
  }, [fieldValue]); // eslint-disable-line

  useEffect(() => {
    if (cacheable && isTouched) {
      prevFieldsRef.current = fields;
      const cachedValue = cache.current.get(String(fieldValue));

      let restoredValue = {
        ...(cachedValue ? cachedValue : getDefaultValues()),
      };

      restoredValue = omit(restoredValue, [conditionFieldName]);
      for (const key of Object.keys(flatten(restoredValue))) {
        form.resetField(`${parentName}${key}`, { keepTouched: false });
        form.setValue(`${parentName}${key}`, get(restoredValue, key) || "");
      }
    }
  }, [fields]); // eslint-disable-line

  return <FormRenderer fields={fields} parentFieldName={parentFieldName} />;
};
